import React, { useMemo, useCallback, useState } from "react";
import "./WeekCalendarView.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import toast from "react-hot-toast";
import { bookingsAPI } from "../../services/api";
import {
  formatDateStr,
  formatCurrency,
  detectShiftType,
  calculatePriceByShift,
} from "./calendarUtils";
import { Room } from "../../types/room";
import { Booking } from "../../types/booking";
import {
  EventInput,
  EventDropArg,
  EventClickArg,
  EventContentArg,
} from "@fullcalendar/core";
import { EventResizeDoneArg } from "@fullcalendar/interaction";

interface WeekCalendarViewProps {
  selectedRoom: Room;
  allBookings: Booking[];
  canManageBookings: boolean;
  onBackToRooms: () => void;
  onBookingUpdated: () => Promise<void>;
  rooms: Room[];
}

function bookingsToEvents(bookings: Booking[], selectedRoomId: string): EventInput[] {
  const roomBookings = bookings.filter(
    (b) => b.roomId === selectedRoomId || (b.roomId as { _id: string })?._id === selectedRoomId
  );

  const events: EventInput[] = [];

  for (const b of roomBookings) {
    const checkInDate = b.checkInDate?.split("T")[0] || b.checkInDate;
    const checkOutDate = b.checkOutDate?.split("T")[0] || b.checkOutDate;
    const start = `${checkInDate}T${b.checkInTime}:00`;
    const end = `${checkOutDate}T${b.checkOutTime}:00`;

    // Color by status
    const isConfirmed = (b as Booking & { status?: string }).status === "confirmed";
    events.push({
      id: b._id,
      title: b.shiftLabel || b.appliedRateType || "Booking",
      start,
      end,
      backgroundColor: isConfirmed ? "#C84B31" : "#E07055",
      borderColor: isConfirmed ? "#C84B31" : "#E07055",
      textColor: "#ffffff",
      extendedProps: {
        booking: b,
        isCleaning: false,
      },
    });

    // Cleaning block (45 min after checkout)
    const checkOut = new Date(`${checkOutDate}T${b.checkOutTime}`);
    const cleaningEnd = new Date(checkOut.getTime() + 45 * 60 * 1000);
    events.push({
      id: `clean-${b._id}`,
      title: "Vệ sinh 45'",
      start: checkOut.toISOString(),
      end: cleaningEnd.toISOString(),
      backgroundColor: "transparent",
      borderColor: "transparent",
      textColor: "transparent",
      editable: false,
      extendedProps: {
        isCleaning: true,
      },
    });
  }

  return events;
}

export default function WeekCalendarView({
  selectedRoom,
  allBookings,
  canManageBookings,
  onBackToRooms,
  onBookingUpdated,
  rooms,
}: WeekCalendarViewProps): React.ReactElement {
  const [selectedBooking, setSelectedBooking] = useState<(Booking & { status?: string; guestName?: string }) | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);

  const events = useMemo(
    () => bookingsToEvents(allBookings, selectedRoom._id),
    [allBookings, selectedRoom._id]
  );

  const handleEventDrop = useCallback(
    async (info: EventDropArg) => {
      if (!canManageBookings) {
        info.revert();
        return;
      }

      const b = info.event.extendedProps.booking as (Booking & { status?: string }) | undefined;
      if (!b) {
        info.revert();
        return;
      }

      const newStart = info.event.start;
      const newEnd = info.event.end;
      if (!newStart || !newEnd) {
        info.revert();
        return;
      }

      const newCheckInDate = formatDateStr(newStart);
      const newCheckInTime = newStart.toTimeString().slice(0, 5);
      const newCheckOutDate = formatDateStr(newEnd);
      const newCheckOutTime = newEnd.toTimeString().slice(0, 5);

      const room = rooms.find((r) => r._id === selectedRoom._id);
      if (!room) {
        info.revert();
        return;
      }

      const newShiftType = detectShiftType(newCheckInDate, newCheckInTime, newCheckOutDate, newCheckOutTime);
      const newPriceResult = calculatePriceByShift(newShiftType, newCheckInDate, newCheckInTime, newCheckOutDate, newCheckOutTime, room);
      const newFinalPrice = newPriceResult.price * (1 - (b.discount || 0) / 100);

      const oldPrice = formatCurrency(b.finalPrice || 0);
      const newPrice = formatCurrency(newFinalPrice);

      const confirmed = window.confirm(
        `Di chuyen booking:\n` +
        `${b.checkInDate} ${b.checkInTime} → ${newCheckInDate} ${newCheckInTime}\n` +
        `${b.checkOutDate} ${b.checkOutTime} → ${newCheckOutDate} ${newCheckOutTime}\n\n` +
        `Gia cu: ${oldPrice}\n` +
        `Gia moi: ${newPrice} (${newPriceResult.shiftLabel})\n\n` +
        `Xac nhan?`
      );

      if (!confirmed) {
        info.revert();
        return;
      }

      try {
        await bookingsAPI.update(b._id, {
          checkInDate: newCheckInDate,
          checkInTime: newCheckInTime,
          checkOutDate: newCheckOutDate,
          checkOutTime: newCheckOutTime,
          shiftLabel: newPriceResult.shiftLabel,
          appliedRateType: newPriceResult.rateType,
          calculatedPrice: newPriceResult.price,
          finalPrice: newFinalPrice,
        });
        toast.success("Da cap nhat lich dat phong!");
        onBookingUpdated();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message || "Loi khi cap nhat");
        info.revert();
      }
    },
    [canManageBookings, rooms, selectedRoom._id, onBookingUpdated]
  );

  const handleEventResize = useCallback(
    async (info: EventResizeDoneArg) => {
      if (!canManageBookings) {
        info.revert();
        return;
      }

      const b = info.event.extendedProps.booking as Booking | undefined;
      if (!b) {
        info.revert();
        return;
      }

      const newEnd = info.event.end;
      if (!newEnd) {
        info.revert();
        return;
      }

      // Keep original checkIn, only change checkOut
      const checkInDate = b.checkInDate?.split("T")[0] || b.checkInDate;
      const checkInTime = b.checkInTime;
      const newCheckOutDate = formatDateStr(newEnd);
      const newCheckOutTime = newEnd.toTimeString().slice(0, 5);

      const room = rooms.find((r) => r._id === selectedRoom._id);
      if (!room) {
        info.revert();
        return;
      }

      const newShiftType = detectShiftType(checkInDate, checkInTime, newCheckOutDate, newCheckOutTime);
      const newPriceResult = calculatePriceByShift(newShiftType, checkInDate, checkInTime, newCheckOutDate, newCheckOutTime, room);
      const newFinalPrice = newPriceResult.price * (1 - (b.discount || 0) / 100);

      const oldPrice = formatCurrency(b.finalPrice || 0);
      const newPrice = formatCurrency(newFinalPrice);

      const confirmed = window.confirm(
        `Thay doi thoi gian tra phong:\n` +
        `${b.checkOutDate} ${b.checkOutTime} → ${newCheckOutDate} ${newCheckOutTime}\n\n` +
        `Gia cu: ${oldPrice}\n` +
        `Gia moi: ${newPrice} (${newPriceResult.shiftLabel})\n\n` +
        `Xac nhan?`
      );

      if (!confirmed) {
        info.revert();
        return;
      }

      try {
        await bookingsAPI.update(b._id, {
          checkOutDate: newCheckOutDate,
          checkOutTime: newCheckOutTime,
          shiftLabel: newPriceResult.shiftLabel,
          appliedRateType: newPriceResult.rateType,
          calculatedPrice: newPriceResult.price,
          finalPrice: newFinalPrice,
        });
        toast.success("Da cap nhat thoi gian tra phong!");
        onBookingUpdated();
      } catch (error: unknown) {
        const err = error as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message || "Loi khi cap nhat");
        info.revert();
      }
    },
    [canManageBookings, rooms, selectedRoom._id, onBookingUpdated]
  );

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const props = info.event.extendedProps;
      if (props.isCleaning) return;
      const b = props.booking as (Booking & { status?: string; guestName?: string }) | undefined;
      if (!b) return;
      setSelectedBooking(b);
      setShowDetailModal(true);
    },
    []
  );

  const handleDeleteBooking = async (): Promise<void> => {
    if (!selectedBooking) return;
    const confirmed = window.confirm(
      `Xac nhan xoa booking?\n${selectedBooking.roomName || selectedRoom.name}\nCheck-in: ${selectedBooking.checkInDate} ${selectedBooking.checkInTime}\nCheck-out: ${selectedBooking.checkOutDate} ${selectedBooking.checkOutTime}`
    );
    if (!confirmed) return;
    try {
      await bookingsAPI.delete(selectedBooking._id);
      toast.success("Da xoa lich dat phong!");
      setShowDetailModal(false);
      setSelectedBooking(null);
      onBookingUpdated();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Loi khi xoa lich dat phong");
    }
  };

  const renderEventContent = (eventInfo: EventContentArg): React.ReactElement => {
    if (eventInfo.event.extendedProps.isCleaning) {
      return (
        <div
          style={{
            height: "100%",
            background: "repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#f8fafc 3px,#f8fafc 8px)",
            borderRadius: "4px",
            display: "flex",
            alignItems: "flex-start",
            padding: "2px 5px",
            fontSize: "10px",
            color: "#64748b",
            lineHeight: "1.4",
          }}
        >
          🧹 Vệ sinh 45'
        </div>
      );
    }

    const b = eventInfo.event.extendedProps.booking as Booking | undefined;
    if (!b) return <div className="px-1 text-xs">{eventInfo.event.title}</div>;

    return (
      <div className="px-1.5 py-1 overflow-hidden h-full flex flex-col gap-0.5">
        <div className="text-xs font-bold leading-tight">
          {eventInfo.timeText}
        </div>
        <div className="text-xs leading-tight flex items-center gap-0.5">
          🔒 Đã đặt
        </div>
        <div className="text-xs font-semibold leading-tight opacity-90">
          {formatCurrency(b.finalPrice || 0)}
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="max-w-3xl mx-auto">
      {/* Back link */}
      <button
        onClick={onBackToRooms}
        className="inline-flex items-center gap-1 text-sm text-terracotta hover:underline mb-5"
        style={{ color: "#C07050" }}
      >
        ← Quay lại Phòng
      </button>

      {/* Calendar section heading */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          🕐 Đặt Phòng
        </h3>
      </div>

      {/* FullCalendar */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-2 mb-8">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "timeGridWeek,dayGridMonth",
          }}
          buttonText={{
            today: "Today",
            month: "Month",
            week: "Week",
          }}
          locale="vi"
          events={events}
          editable={canManageBookings}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          scrollTime="06:00:00"
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          allDaySlot={false}
          nowIndicator={true}
          height="calc(100vh - 400px)"
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          slotLabelContent={(arg: { date: Date }) => {
            const h = arg.date.getHours();
            return <span>{String(h).padStart(2, "0")} giờ</span>;
          }}
          dayMaxEvents={true}
          firstDay={1}
          eventOverlap={false}
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
        />
      </div>
    </div>

      {/* Booking Detail Modal */}
      {showDetailModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Chi Tiet Lich Dat Phong</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Phong:</span>
                  <span className="font-semibold">{selectedBooking.roomName || selectedRoom.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Check-in:</span>
                  <span className="font-semibold">{selectedBooking.checkInDate} {selectedBooking.checkInTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Check-out:</span>
                  <span className="font-semibold">{selectedBooking.checkOutDate} {selectedBooking.checkOutTime}</span>
                </div>
                {selectedBooking.shiftLabel && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Loai ca:</span>
                    <span className="font-semibold">{selectedBooking.shiftLabel}</span>
                  </div>
                )}
                {selectedBooking.durationHours && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Thoi gian:</span>
                    <span className="font-semibold">{selectedBooking.durationHours} gio</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Gia:</span>
                  <span className="font-bold text-blue-700">{formatCurrency(selectedBooking.finalPrice || 0)}</span>
                </div>
                {(selectedBooking.discount ?? 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Giam gia:</span>
                    <span className="font-semibold text-green-600">{selectedBooking.discount}%</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Trang thai:</span>
                  <span className={`font-semibold ${
                    selectedBooking.status === "confirmed" ? "text-green-600" :
                    selectedBooking.status === "cancelled" ? "text-red-600" : "text-yellow-600"
                  }`}>
                    {selectedBooking.status === "confirmed" ? "Da xac nhan" :
                     selectedBooking.status === "cancelled" ? "Da huy" : "Cho xac nhan"}
                  </span>
                </div>
                {selectedBooking.notes && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ghi chu:</span>
                    <span className="font-semibold text-right max-w-xs">{selectedBooking.notes}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                {canManageBookings && (
                  <button
                    onClick={handleDeleteBooking}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 font-semibold"
                  >
                    Xoa Lich
                  </button>
                )}
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedBooking(null); }}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Dong
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
